import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../../styles/DeviceMap.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Only when there is nothing at all to go on.
const FALLBACK_CENTER = [39.8283, -98.5795];
const FALLBACK_ZOOM = 4;
const NEARBY_ZOOM = 12;
// Close enough to read the street. A single screen's page is not a
// choose-between-these map -- the choosing is done -- so it wants the block,
// not the district. fitBounds on a one-point bounds lands on its maxZoom of
// 14, which shows a neighbourhood and leaves the pin looking unplaced.
const FOCUS_ZOOM = 16;

function isValidLocation(device) {
    const lat = Number(device.latitude);
    const lng = Number(device.longitude);
    return (
        device.latitude !== null && device.latitude !== undefined &&
        device.longitude !== null && device.longitude !== undefined &&
        !Number.isNaN(lat) && !Number.isNaN(lng) &&
        Math.abs(lat) <= 90 && Math.abs(lng) <= 180
    );
}

function money(value) {
    if (value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isNaN(n) ? null : `$${n.toFixed(2)} per day`;
}

/**
 * Decides where the map looks.
 *
 * Order changed deliberately. It used to fit the pins first and only ask for
 * the viewer's location when there were none — but this is a browse-by-location
 * product, so "where am I" is the more useful default. One badly geocoded
 * screen on the other side of the world used to drag the whole viewport with
 * it, which is exactly what happened with a seeded address of "1 High Street":
 * Nominatim's top hit for that string is in Hong Kong.
 *
 * MapContainer's `center`/`zoom` are initial values only — Leaflet ignores
 * later changes — so this has to be imperative.
 */
function Viewport({ devices, locate, focus, onModeChange, showAllRef }) {
    const map = useMap();
    const [mode, setMode] = useState(null); // "focus" | "near" | "all" | "fallback"

    const showAll = useCallback(() => {
        if (!devices.length) return;
        map.fitBounds(
            L.latLngBounds(devices.map((d) => [Number(d.latitude), Number(d.longitude)])),
            { padding: [48, 48], maxZoom: 14 }
        );
        setMode("all");
    }, [devices, map]);

    // A ref, not state. This used to be `onViewChange({ mode, showAll })` in an
    // effect, and it locked the browser: the parent stored the object in state,
    // re-rendered, rebuilt the filtered device array, which changed showAll's
    // identity, which re-ran this effect, which set state again. An unbounded
    // render loop -- and because it saturates the main thread, nothing on the
    // page responds to a click, including the fixed nav above it.
    //
    // Only `mode` crosses the boundary now, and it is a string, so React bails
    // out of the re-render when it has not changed.
    showAllRef.current = showAll;

    useEffect(() => {
        onModeChange(mode);
    }, [mode, onModeChange]);

    useEffect(() => {
        if (mode) return; // the viewer has taken over; do not yank it back

        // FOCUS: one known screen, centred on it, immediately.
        //
        // This is the whole fix for the screen's own page. The marker was
        // being rendered correctly all along -- and then the map asked the
        // browser where the VIEWER was and centred there, so you got a map of
        // your own town with the pin somewhere off the edge. That is the right
        // default for browse, where "near me" is the question, and exactly
        // wrong for "show me this one", where it is already answered.
        //
        // It also skips the geolocation prompt, which matters on its own:
        // being asked for your location to look at a screen you have already
        // chosen is a permission dialog with nothing behind it.
        if (focus && devices.length) {
            const only = devices[0];
            map.setView(
                [Number(only.latitude), Number(only.longitude)], FOCUS_ZOOM
            );
            setMode("focus");
            return undefined;
        }

        if (!locate || !navigator.geolocation) {
            if (devices.length) showAll();
            else setMode("fallback");
            return;
        }

        let cancelled = false;
        navigator.geolocation.getCurrentPosition(
            (position) => {
                if (cancelled) return;
                map.setView(
                    [position.coords.latitude, position.coords.longitude],
                    NEARBY_ZOOM
                );
                setMode("near");
            },
            () => {
                // Denied, unavailable, or timed out. Fall back to the screens
                // themselves rather than leaving the viewer on an empty ocean.
                if (cancelled) return;
                if (devices.length) showAll();
                else setMode("fallback");
            },
            { timeout: 8000, maximumAge: 300000 }
        );
        return () => { cancelled = true; };
    }, [devices, focus, locate, map, mode, showAll]);

    return null;
}

/**
 * Wheel zoom while the pointer is over the map.
 *
 * The wheel is enabled on `mouseenter` and disabled on `mouseleave`, so the
 * map only ever takes a scroll the pointer is actually aimed at, and the page
 * scrolls normally everywhere else. Keyboard focus arms it too, so tabbing to
 * the map and using the wheel behaves the same.
 *
 * The cost of hovering rather than clicking to arm: a scroll that passes over
 * the map on its way down the page zooms instead of scrolling past. That is
 * the trade this browse page wants — the map is the primary thing on it.
 *
 * Imperative because react-leaflet passes `scrollWheelZoom` as an initial
 * Leaflet option; changing the prop later does not re-enable the handler.
 * `mouseenter`/`mouseleave` rather than Leaflet's own `mouseover`/`mouseout`,
 * which also fire when the pointer crosses onto a marker inside the map — so
 * the wheel would switch off over exactly the pins people zoom towards.
 */
function WheelZoom() {
    const map = useMap();

    useEffect(() => {
        const container = map.getContainer();
        const arm = () => map.scrollWheelZoom.enable();
        const disarm = () => map.scrollWheelZoom.disable();

        container.addEventListener("mouseenter", arm);
        container.addEventListener("focus", arm);
        container.addEventListener("mouseleave", disarm);
        container.addEventListener("blur", disarm);
        return () => {
            container.removeEventListener("mouseenter", arm);
            container.removeEventListener("focus", arm);
            container.removeEventListener("mouseleave", disarm);
            container.removeEventListener("blur", disarm);
            // The handler outlives this effect if the map is kept and only the
            // component remounts, so leave it in the state it started in.
            map.scrollWheelZoom.disable();
        };
    }, [map]);

    return null;
}

export default function DeviceMap({
    devices = [],
    locate = true,
    height = 400,
    // Centre on the first screen and stay there, with no geolocation prompt.
    // For a single screen's own page, where "where is it" is the question the
    // map exists to answer.
    focus = false,
}) {
    // Memoised so its identity is stable between renders. A fresh array each
    // render is what made the callback above unstable in the first place.
    const validDevices = useMemo(() => devices.filter(isValidLocation), [devices]);
    const missing = devices.length - validDevices.length;

    const [mode, setMode] = useState(null);
    const onModeChange = useCallback((next) => setMode(next), []);
    const showAllRef = useRef(null);

    return (
        <div className="dm">
            <MapContainer
                center={FALLBACK_CENTER}
                zoom={FALLBACK_ZOOM}
                // Off at mount; WheelZoom below turns it on while the pointer
                // is over the map.
                scrollWheelZoom={false}
                style={{ height: `${height}px`, width: "100%", borderRadius: "var(--r-lg, 8px)" }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Viewport
                    devices={validDevices}
                    locate={locate}
                    focus={focus}
                    onModeChange={onModeChange}
                    showAllRef={showAllRef}
                />
                <WheelZoom />

                {validDevices.map((device) => (
                    <Marker
                        key={device.id}
                        position={[Number(device.latitude), Number(device.longitude)]}
                    >
                        <Popup>
                            {/* react-leaflet renders popup children through a
                                portal that stays inside the React tree, so the
                                router context reaches this <Link> and it
                                navigates rather than reloading the app. */}
                            <div className="dm__popup">
                                <strong className="dm__popup-name">
                                    {device.name || `Screen #${device.id}`}
                                </strong>
                                {device.public_code && (
                                    <span className="dm__popup-code">
                                        {device.public_code}
                                    </span>
                                )}
                                {/* `address` on the browse list, `location` on
                                    the detail endpoint -- the same field under
                                    two names, which is why this line used to
                                    disappear on a single screen's page. */}
                                {(device.address || device.location) && (
                                    <span className="dm__popup-addr">
                                        {device.address || device.location}
                                    </span>
                                )}
                                <span className="dm__popup-rate">
                                    {money(device.daily_rate ?? device.rate) || "Rate not set"}
                                </span>
                                <Link to={`/devices/${device.id}`} className="dm__popup-link">
                                    View details
                                </Link>
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>

            <div
                className="wb-row"
                style={{ marginTop: "var(--s-2, 8px)", justifyContent: "space-between" }}
            >
                <span className="wb-hint">
                    {mode === "focus" && "Approximate location."}
                    {mode === "near" && "Showing your area."}
                    {mode === "all" && "Showing every listed screen."}
                    {mode === "fallback" && "Turn on location to see screens near you."}
                </span>

                {mode === "near" && validDevices.length > 0 && (
                    <button
                        type="button"
                        className="wb-btn wb-btn--ghost wb-btn--sm"
                        onClick={() => showAllRef.current && showAllRef.current()}
                    >
                        Show all screens
                    </button>
                )}
            </div>

            {missing > 0 && (
                <div className="wb-hint">
                    {missing === 1
                        ? "1 screen is not shown on the map because its address has not been located yet."
                        : `${missing} screens are not shown on the map because their addresses have not been located yet.`}
                </div>
            )}
        </div>
    );
}
